import {Directive, ElementRef, forwardRef, HostListener, Input, Renderer2} from '@angular/core';
import {NG_VALUE_ACCESSOR} from '@angular/forms';
import {SimpleUnit} from '@core/interfaces/system/system-common';
import {FormatsService} from '@core/utils';

@Directive({
    selector: '[ngxFormattedNumber]',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => FormatNumberDirective),
            multi: true,
        },
    ],
})
/** Formats currency or percentage values when the user focuses and blurs an input element,
 *  or when the input control has data bound to it via patchValue.*/
export class FormatNumberDirective {
    /** The currency unit being evaluated */
    @Input() currencyUnit: SimpleUnit;
    /** Flag if unit being evaluated is a percentage */
    @Input() percentage: boolean = false;

    private onChange = (value: any) => {};
    private onTouched = () => {};

    innerValue: string; // keep a record of the value, full precision with no units

    constructor(private el: ElementRef, private renderer: Renderer2) {}

    // This method is called when the FormControl's value is set via patchValue.
    writeValue(value: any): void {
        this.renderer.setProperty(this.el.nativeElement, 'value', value);
        const blurEvent = {
            srcElement: {
                value: value + '',
            },
            wasPatched: true,
        };
        if (!!value || value === 0) {
            this.onBlur(blurEvent);
        }
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    @HostListener('focus', ['$event']) onFocus(event) {
        // strip out all $,%,K,B,M chars and get raw values, a number as a string
        if (this.currencyUnit || this.percentage) {
            const inputValue = event.srcElement.value;
            this.innerValue = FormatsService.convertCurrencyStringToValue(inputValue) + '';
            event.srcElement.value = this.innerValue; // set the value of the input control
            event.srcElement.select(); // select all text to allow user to update
        }
    }

    @HostListener('blur', ['$event']) onBlur(event) {
        this.innerValue = event.srcElement.value; // user left cell, save raw number value

        const displayValue = this.getFormattedValue();

        if (event.wasPatched) {
            // input was patched by data binding
            this.el.nativeElement.value = displayValue;
        } else {
            // user blurred the control
            event.srcElement.value = displayValue;
            // emit the onChange event so that the chart data source can pick up
            // the changes and update the bar graphs with the new data
            this.onChange(this.innerValue);
        }
    }

    /** Formats the input control value into a user readable format */
    private getFormattedValue(): string {
        let cleanInput = this.innerValue.replace(/[^\d.-]/g, ''); // strip out all non numeric chars, keep decimal
        let formattedValue = '';
        if (this.currencyUnit) {
            // set the input element value to the readable format ie $13.24M
            formattedValue = FormatsService.prepareCostValueFromUnit(
                parseFloat(cleanInput).toFixed(2),
                this.currencyUnit,
            );
        }

        if (this.percentage) {
            // cap any larger values than 100%
            if (+cleanInput > 100) {
                cleanInput = '100';
                this.innerValue = cleanInput;
            }
            // set the input element value to the readable format ie 90.15%
            formattedValue = FormatsService.preparePercentageLabel(+cleanInput, 3); //+ forces string into number
        }
        return formattedValue;
    }
}
